'use client';

import { useCart } from '@/lib/cart';
import { formatPrice } from '@/lib/format';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useEffect } from 'react';
import Link from 'next/link';

export default function CartDrawer() {
  const { items, isOpen, closeCart, updateQuantity, removeItem, total, count } = useCart();

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) closeCart();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeCart]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[80] bg-[#2B1810]/70 backdrop-blur-sm transition-opacity duration-500 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 bottom-0 z-[81] w-full sm:w-[460px] max-w-full bg-[#F8F1E4] shadow-2xl flex flex-col transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#2B1810] to-[#5C2E03] px-5 py-5 flex items-center justify-between border-b border-[#C9A961]/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border border-[#C9A961] bg-[#2B1810] flex items-center justify-center">
              <ShoppingBag size={18} className="text-[#C9A961]" />
            </div>
            <div>
              <h2 className="font-display text-[#F8F1E4] text-lg tracking-[0.1em] font-semibold">
                Votre Panier
              </h2>
              <p className="text-[#C9A961]/80 text-[10px] tracking-[0.25em] uppercase font-serif-display">
                {count} article{count > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="w-9 h-9 rounded-full border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-[#2B1810] transition-all duration-300 flex items-center justify-center"
            aria-label="Fermer le panier"
          >
            <X size={18} />
          </button>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-24 h-24 rounded-full border-2 border-[#C9A961]/40 flex items-center justify-center mb-5">
                <ShoppingBag size={36} className="text-[#C9A961]/60" />
              </div>
              <h3 className="font-display text-[#2B1810] text-xl mb-2">Votre panier est vide</h3>
              <p className="text-[#8B4513]/70 text-sm font-serif-display italic mb-6 leading-relaxed">
                Découvrez nos collections d'exception et ajoutez vos pièces préférées à votre panier.
              </p>
              <button
                onClick={closeCart}
                className="px-6 py-3 bg-[#2B1810] text-[#C9A961] hover:bg-[#5C2E03] transition-colors duration-300 text-xs tracking-[0.25em] uppercase font-medium rounded-sm"
              >
                Découvrir la Boutique
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="bg-white border border-[#C9A961]/20 rounded-lg p-3 flex gap-3 group hover:border-[#C9A961]/50 transition-colors"
                >
                  <div className="w-20 h-20 rounded-md overflow-hidden bg-[#F8F1E4] flex-shrink-0 border border-[#C9A961]/15">
                    <img
                      src={item.imageSrc}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#C9A961] font-serif-display mb-0.5">
                      {item.categoryLabel}
                    </p>
                    <h4 className="font-display text-[#2B1810] text-sm leading-tight font-medium truncate">
                      {item.name}
                    </h4>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5 border border-[#C9A961]/30 rounded-full bg-[#F8F1E4]">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center text-[#2B1810] hover:text-[#C9A961] transition-colors"
                          aria-label="Réduire la quantité"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-xs font-semibold text-[#2B1810] min-w-[20px] text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center text-[#2B1810] hover:text-[#C9A961] transition-colors"
                          aria-label="Augmenter la quantité"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-[#2B1810] text-sm font-semibold">
                          {formatPrice(item.price * item.quantity)}
                        </div>
                        {item.quantity > 1 && (
                          <div className="text-[10px] text-[#8B4513]/60 font-serif-display italic">
                            {formatPrice(item.price)} / pièce
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="text-[#8B4513]/40 hover:text-red-700 transition-colors self-start"
                    aria-label={`Retirer ${item.name} du panier`}
                  >
                    <Trash2 size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer with total + checkout */}
        {items.length > 0 && (
          <div className="border-t border-[#C9A961]/30 bg-white px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[#8B4513]/80 text-xs tracking-[0.2em] uppercase font-serif-display">
                Sous-total
              </span>
              <span className="font-display text-2xl font-semibold text-[#2B1810]">
                {formatPrice(total)}
              </span>
            </div>
            <p className="text-[10px] text-[#8B4513]/60 font-serif-display italic text-center">
              Livraison & taxes calculées au paiement
            </p>
            <Link
              href="/checkout"
              onClick={closeCart}
              className="group relative block w-full px-6 py-4 bg-[#C9A961] text-[#2B1810] font-medium text-xs sm:text-sm tracking-[0.25em] uppercase overflow-hidden transition-all duration-500 hover:bg-[#D4A017] hover:shadow-[0_8px_30px_rgba(212,160,23,0.4)] rounded-sm text-center"
            >
              <span className="relative z-10 flex items-center justify-center gap-2">
                Passer Commande
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </Link>
            <button
              onClick={closeCart}
              className="w-full text-center text-[10px] text-[#8B4513]/70 hover:text-[#2B1810] tracking-[0.2em] uppercase font-serif-display transition-colors"
            >
              Continuer mes achats
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
